import React from 'react';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import type { DocMode } from '../../types/e2i';
import LineItemPicker from './LineItemPicker';

// ---------------------------------------------------------------- rows as typed

export interface CostTypeRow { code: string; name: string; markup: string; isNew?: boolean }
export interface JobTypeRow { code: string; name: string; salesItem: string | null; account: string | null; isNew?: boolean }
export interface StageRow { name: string; role: string; minAmount: string }

const codeText = (v: string, max: number) => v.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, max);

const RowBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }} sx={{ borderBottom: '1px solid #eceff1', pb: 1.5 }}>
    {children}
  </Stack>
);

/** A select of the PRPO roles; a saved role the list lacks stays choosable. */
const RoleSelect: React.FC<{ id: string; label: string; value: string; roles: string[]; onChange: (v: string) => void; required?: boolean; allowEmpty?: string }> = ({
  id, label, value, roles, onChange, required, allowEmpty,
}) => {
  const options = value && !roles.includes(value) ? [value, ...roles] : roles;
  return (
    <TextField
      id={id}
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      fullWidth
      SelectProps={{ displayEmpty: !!allowEmpty }}
      InputLabelProps={allowEmpty ? { shrink: true } : undefined}
    >
      {allowEmpty ? <MenuItem value=""><em>{allowEmpty}</em></MenuItem> : null}
      {options.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
    </TextField>
  );
};

export { RoleSelect };

// ---------------------------------------------------------------- cost types

export const CostTypesEditor: React.FC<{ rows: CostTypeRow[]; onChange: (rows: CostTypeRow[]) => void }> = ({ rows, onChange }) => {
  const set = (i: number, patch: Partial<CostTypeRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Stack spacing={1.5}>
      {rows.map((r, i) => (
        <RowBox key={i}>
          <TextField
            id={`e2i-set-ct-code-${i}`}
            label="Code"
            value={r.code}
            onChange={(e) => set(i, { code: codeText(e.target.value, 10) })}
            disabled={!r.isNew}
            helperText={r.isNew ? 'Up to 10 letters or digits' : undefined}
            sx={{ width: { md: 160 } }}
            required
          />
          <TextField id={`e2i-set-ct-name-${i}`} label="Name" value={r.name} onChange={(e) => set(i, { name: e.target.value.slice(0, 60) })} fullWidth required />
          <TextField
            id={`e2i-set-ct-markup-${i}`}
            label="Default markup %"
            value={r.markup}
            onChange={(e) => set(i, { markup: e.target.value })}
            inputProps={{ inputMode: 'decimal' }}
            sx={{ width: { md: 180 } }}
          />
          <IconButton aria-label={`Remove cost type ${r.code || i + 1}`} onClick={() => onChange(rows.filter((_x, j) => j !== i))} sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </RowBox>
      ))}
      <Box>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onChange([...rows, { code: '', name: '', markup: '0', isNew: true }])}>Add cost type</Button>
      </Box>
    </Stack>
  );
};

// ---------------------------------------------------------------- job types

export const JobTypesEditor: React.FC<{ rows: JobTypeRow[]; onChange: (rows: JobTypeRow[]) => void; companyId: number; docMode: DocMode }> = ({ rows, onChange, companyId, docMode }) => {
  const set = (i: number, patch: Partial<JobTypeRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Stack spacing={1.5}>
      {rows.length ? null : <Typography variant="body2" color="text.secondary">No job types yet: a job then needs no type, and its lines take the company defaults (SAP documents tab).</Typography>}
      {rows.map((r, i) => (
        <RowBox key={i}>
          <TextField
            id={`e2i-set-jt-code-${i}`}
            label="Code"
            value={r.code}
            onChange={(e) => set(i, { code: codeText(e.target.value, 20) })}
            disabled={!r.isNew}
            sx={{ width: { md: 160 } }}
            required
          />
          <TextField id={`e2i-set-jt-name-${i}`} label="Name" value={r.name} onChange={(e) => set(i, { name: e.target.value.slice(0, 80) })} fullWidth required />
          <Box sx={{ width: { xs: '100%', md: 360 }, flex: '0 0 auto' }}>
            <LineItemPicker
              id={`e2i-set-jt-target-${i}`}
              companyId={companyId}
              docMode={docMode}
              value={docMode === 'Service' ? r.account : r.salesItem}
              onChange={(code) => set(i, docMode === 'Service' ? { account: code } : { salesItem: code })}
              helperText={docMode === 'Service' ? 'The revenue account of its lines (else the company default)' : 'The sales item of its lines (else the company default)'}
            />
          </Box>
          <IconButton aria-label={`Remove job type ${r.code || i + 1}`} onClick={() => onChange(rows.filter((_x, j) => j !== i))} sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </RowBox>
      ))}
      <Box>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onChange([...rows, { code: '', name: '', salesItem: null, account: null, isNew: true }])}>Add job type</Button>
      </Box>
    </Stack>
  );
};

// ---------------------------------------------------------------- approval stages

export const StagesEditor: React.FC<{
  idPrefix: string;
  rows: StageRow[];
  onChange: (rows: StageRow[]) => void;
  roles: string[];
  withMinimum?: boolean;
  currency?: string;
  addLabel: string;
}> = ({ idPrefix, rows, onChange, roles, withMinimum, currency, addLabel }) => {
  const set = (i: number, patch: Partial<StageRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const moveUp = (i: number) => {
    if (i <= 0) return;
    const list = [...rows];
    [list[i - 1], list[i]] = [list[i], list[i - 1]];
    onChange(list);
  };
  return (
    <Stack spacing={1.5}>
      {rows.length ? null : <Typography variant="body2" color="text.secondary">No stage: approved as soon as it is submitted.</Typography>}
      {rows.map((r, i) => (
        <RowBox key={i}>
          <Typography variant="body2" sx={{ pt: { md: 1 }, minWidth: 64 }}>Stage {i + 1}</Typography>
          <TextField id={`${idPrefix}-name-${i}`} label="Stage name" value={r.name} onChange={(e) => set(i, { name: e.target.value.slice(0, 80) })} fullWidth required />
          <Box sx={{ width: { xs: '100%', md: 280 }, flex: '0 0 auto' }}>
            <RoleSelect id={`${idPrefix}-role-${i}`} label="Approver role" value={r.role} roles={roles} onChange={(v) => set(i, { role: v })} required />
          </Box>
          {withMinimum ? (
            <TextField
              id={`${idPrefix}-min-${i}`}
              label={`From total ${currency || ''}`.trim()}
              value={r.minAmount}
              onChange={(e) => set(i, { minAmount: e.target.value })}
              inputProps={{ inputMode: 'decimal' }}
              placeholder="Every quotation"
              sx={{ width: { md: 200 } }}
            />
          ) : null}
          <Stack direction="row" sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>
            <IconButton aria-label={`Move stage ${i + 1} up`} onClick={() => moveUp(i)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
            <IconButton aria-label={`Remove stage ${i + 1}`} onClick={() => onChange(rows.filter((_x, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Stack>
        </RowBox>
      ))}
      <Box>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onChange([...rows, { name: '', role: '', minAmount: '' }])}>{addLabel}</Button>
      </Box>
    </Stack>
  );
};
